import { useState, useEffect, useCallback } from "react";
import Button from "@components/ui/buttons/Button";
import { useDataStore } from "@store/user_org";
import { whatsappApi } from "@services/api/organizations";
import { showNotification } from "context/notification.service";

// Facebook SDK types
declare global {
  interface Window {
    FB: {
      init: (params: Record<string, unknown>) => void;
      login: (
        callback: (response: FBLoginResponse) => void,
        params: Record<string, unknown>,
      ) => void;
    };
    fbAsyncInit: () => void;
  }
}

interface FBLoginResponse {
  authResponse?: {
    code?: string;
    accessToken?: string;
  };
  status: string;
}

export function WhatsAppSettings() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);

  const wa = orgData?.whatsapp;

  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [metaConfig, setMetaConfig] = useState<{
    app_id: string;
    config_id: string;
  } | null>(null);

  // Load Meta config + Facebook SDK on mount
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const config = await whatsappApi.getConfig();
        if (cancelled) return;
        setMetaConfig(config);

        // Load Facebook SDK
        if (!document.getElementById("facebook-jssdk")) {
          const script = document.createElement("script");
          script.id = "facebook-jssdk";
          script.src = "https://connect.facebook.net/en_US/sdk.js";
          script.async = true;
          script.defer = true;
          script.crossOrigin = "anonymous";
          document.body.appendChild(script);
        }

        window.fbAsyncInit = () => {
          window.FB.init({
            appId: config.app_id,
            autoLogAppEvents: true,
            xfbml: false,
            version: "v21.0",
          });
          if (!cancelled) setSdkReady(true);
        };

        // SDK might already be loaded
        if (window.FB) {
          window.FB.init({
            appId: config.app_id,
            autoLogAppEvents: true,
            xfbml: false,
            version: "v21.0",
          });
          if (!cancelled) setSdkReady(true);
        }
      } catch {
        // Config not available — manual mode or not configured
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEmbeddedSignup = useCallback(() => {
    if (!window.FB || !metaConfig) return;

    setIsConnecting(true);

    window.FB.login(
      async (response: FBLoginResponse) => {
        if (response.authResponse?.code) {
          try {
            const result = await whatsappApi.completeEmbeddedSignup(
              response.authResponse.code,
            );
            if (orgData) await fetchOrgData(orgData.id);
            showNotification({
              type: "success",
              message: `WhatsApp connecté — ${result.business_name} (${result.display_phone})`,
            });
          } catch (err: any) {
            const detail =
              err?.response?.data?.detail || "Échec de la connexion WhatsApp";
            showNotification({ type: "error", message: detail });
          }
        } else {
          showNotification({
            type: "error",
            message: "Connexion annulée ou refusée",
          });
        }
        setIsConnecting(false);
      },
      {
        config_id: metaConfig.config_id,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: "",
          sessionInfoVersion: "2",
        },
      },
    );
  }, [metaConfig, orgData, fetchOrgData]);

  const handleDisconnect = async () => {
    if (!orgData) return;
    setIsDisconnecting(true);
    try {
      await whatsappApi.disconnect();
      await fetchOrgData(orgData.id);
      showNotification({ type: "success", message: "WhatsApp déconnecté" });
    } catch {
      showNotification({
        type: "error",
        message: "Échec de la déconnexion",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="flex flex-col p-8 max-w-2xl">
      <h1
        className="text-hx font-semibold mb-2"
        style={{ fontFamily: "Poppins" }}
      >
        WhatsApp Business
      </h1>
      <p
        className="text-h2 text-neutral-500 mb-8"
        style={{ fontFamily: "Poppins" }}
      >
        Connectez le numéro WhatsApp Business de votre garage pour envoyer et
        recevoir des messages clients directement depuis l'application.
      </p>

      {/* Status card */}
      {wa?.connected ? (
        <div
          className="flex flex-col gap-4 mb-8 p-6 rounded-xl"
          style={{
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
          }}
        >
          <div className="flex flex-row items-center gap-3">
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 999,
                background: "#22C55E",
              }}
            />
            <span
              className="text-h1 font-semibold"
              style={{ fontFamily: "Poppins", color: "#166534" }}
            >
              Connecté
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <span
                className="text-h3 font-medium"
                style={{ fontFamily: "Poppins", color: "#6B7280", width: 140 }}
              >
                Entreprise
              </span>
              <span
                className="text-h2 font-semibold"
                style={{ fontFamily: "Poppins", color: "#111827" }}
              >
                {wa.business_name || "—"}
              </span>
            </div>
            {wa.display_phone && (
              <div className="flex flex-row items-center gap-2">
                <span
                  className="text-h3 font-medium"
                  style={{
                    fontFamily: "Poppins",
                    color: "#6B7280",
                    width: 140,
                  }}
                >
                  Numéro
                </span>
                <span
                  className="text-h2 font-medium"
                  style={{ fontFamily: "Poppins", color: "#111827" }}
                >
                  {wa.display_phone}
                </span>
              </div>
            )}
          </div>

          <button
            onClick={handleDisconnect}
            disabled={isDisconnecting}
            className="text-h3 font-medium hover:underline self-start mt-2"
            style={{ fontFamily: "Poppins", color: "#EF4444" }}
          >
            {isDisconnecting ? "Déconnexion..." : "Déconnecter ce numéro"}
          </button>
        </div>
      ) : (
        <div
          className="flex flex-col items-center gap-6 mb-8 p-8 rounded-xl"
          style={{
            background: "#FAFAFA",
            border: "1px solid #E5E7EB",
          }}
        >
          {/* WhatsApp logo */}
          <div
            className="flex items-center justify-center"
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              background: "#25D366",
            }}
          >
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="white"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </div>

          <div className="flex flex-col items-center gap-2">
            <span
              className="text-h1 font-semibold"
              style={{ fontFamily: "Poppins", color: "#111827" }}
            >
              Connecter WhatsApp Business
            </span>
            <span
              className="text-h3 text-center"
              style={{
                fontFamily: "Poppins",
                color: "#6B7280",
                maxWidth: 400,
              }}
            >
              Connectez votre compte WhatsApp Business ou créez-en un nouveau en
              quelques clics via Meta.
            </span>
          </div>

          <Button
            label={
              isConnecting
                ? "Connexion en cours..."
                : "Connecter avec Facebook"
            }
            onClick={handleEmbeddedSignup}
            disabled={isConnecting || !sdkReady}
          />

          {!sdkReady && !metaConfig && (
            <span
              className="text-h4"
              style={{ fontFamily: "Poppins", color: "#9CA3AF" }}
            >
              Configuration Embedded Signup non disponible
            </span>
          )}
        </div>
      )}

      {/* How it works */}
      <div
        className="flex flex-col gap-4 p-5 rounded-lg"
        style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
      >
        <span
          className="text-h2 font-semibold"
          style={{ fontFamily: "Poppins", color: "#374151" }}
        >
          Comment ça marche
        </span>
        <div className="flex flex-col gap-3">
          {[
            {
              step: "1",
              title: "Connexion",
              desc: 'Cliquez sur "Connecter avec Facebook" et suivez le processus Meta pour créer ou lier votre compte WhatsApp Business.',
            },
            {
              step: "2",
              title: "Réception automatique",
              desc: "Les messages WhatsApp de vos clients apparaissent en temps réel dans la conversation du dossier correspondant.",
            },
            {
              step: "3",
              title: "Réponse depuis l'app",
              desc: 'Répondez directement depuis l\'onglet "WhatsApp" du chat — le client reçoit votre réponse sur WhatsApp.',
            },
            {
              step: "4",
              title: "Gratuit et illimité",
              desc: "Les conversations initiées par le client sont gratuites et illimitées (fenêtre de 24h renouvelable).",
            },
          ].map((item) => (
            <div key={item.step} className="flex flex-row gap-3">
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "#25D366",
                  color: "#FFFFFF",
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {item.step}
              </div>
              <div className="flex flex-col gap-0.5">
                <span
                  className="text-h3 font-semibold"
                  style={{ fontFamily: "Poppins", color: "#374151" }}
                >
                  {item.title}
                </span>
                <span
                  className="text-h4"
                  style={{ fontFamily: "Poppins", color: "#6B7280" }}
                >
                  {item.desc}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
